import type { DisplayRule } from '@/domain/configuration';
import { DISPLAY_RULE_TYPE } from '@/domain/configuration';
import { type DefaultDisplay, DISPLAY_RULE_BAR, DISPLAY_RULE_BUTTON } from '@/domain/configurationV2';
import type { V2LauncherDisplayRule, V2LauncherDisplayRules } from '@/launchers/types';
import displayRuleMatcher from '@/lib/displayRules/displayRuleMatcher';
import getNormalizedUrl from '@/lib/getNormalizedUrl';
import { logger } from '@/lib/logger';

/**
 * What a page's rules ask its chat entry point to be. The bar and the compact button
 * are one slot, so this is the whole answer rather than a visibility flag plus a face:
 * a rule set that hides the bar hides the button with it unless it says otherwise.
 */
export const CHAT_BAR_DISPLAY = {
  /** The chat bar, or -- for a Legacy UI org, or once the shopper dismisses it -- the bubble it collapses into. */
  BAR: 'BAR',
  /** The compact button alone, on a page that would otherwise show the bar or nothing. */
  BUTTON: 'BUTTON',
  /** No entry point at all. Chat stays reachable through Gladly.show() and a launcher click. */
  HIDDEN: 'HIDDEN',
} as const;

export type ChatBarDisplay = (typeof CHAT_BAR_DISPLAY)[keyof typeof CHAT_BAR_DISPLAY];

export interface DisplayRuleVerdict {
  display: ChatBarDisplay;
  displayDelay: number;
}

// The matcher's own shape plus the choice the matched rule carries back.
// id only surfaces in the matcher's error messages.
type UrlRule = DisplayRule & Pick<V2LauncherDisplayRule, 'display'>;

const asUrlRule = (rule: V2LauncherDisplayRule): UrlRule => ({
  type: DISPLAY_RULE_TYPE.URL,
  id: rule.value,
  displayDelay: rule.displayDelay ?? 0,
  content: { matchType: rule.matchType, value: rule.value },
  display: rule.display,
});

function ruleDisplay(display: string | undefined): ChatBarDisplay | null {
  if (display === DISPLAY_RULE_BUTTON) return CHAT_BAR_DISPLAY.BUTTON;
  if (display === DISPLAY_RULE_BAR) return CHAT_BAR_DISPLAY.BAR;
  return null;
}

/**
 * `id` names the rule set's owner in error logs. `defaultDisplay` is what a page shows
 * when no rule decides it -- there are none, or none matches under HIDE_ON_MATCH -- and is
 * the bar when absent. A page SHOW_ON_MATCH leaves unmatched is hidden, not defaulted.
 */
export function evaluateUrlDisplayRules(
  displayRules: V2LauncherDisplayRules | undefined,
  options: { id: string; defaultDisplay?: DefaultDisplay; url?: string },
): DisplayRuleVerdict {
  const fallback = options.defaultDisplay ?? CHAT_BAR_DISPLAY.BAR;
  if (!displayRules) {
    return { displayDelay: 0, display: fallback };
  }

  let matched: UrlRule | null;
  let displayDelay: number;
  try {
    const target = options.url ?? getNormalizedUrl();
    ({ displayDelay, matched } = displayRuleMatcher((displayRules.rules ?? []).map(asUrlRule), { url: target }));
  } catch (error) {
    // urlDisplayRuleMatcher throws on an unknown matchType or an empty URL. Validation
    // drops those rules, so this is reachable only as a bug — log at a level production
    // does not mute.
    logger.error(`[displayRules] hiding "${options.id}": display rule could not be evaluated`, error);
    return { displayDelay: 0, display: CHAT_BAR_DISPLAY.HIDDEN };
  }

  // A matched rule may name the button or the bar in place of its set's polarity. Absent —
  // and any value this build does not know, which is how a config written by a newer admin
  // reads here — leaves the polarity alone, so the rule acts exactly as it did before the
  // field existed.
  const onMatch = ruleDisplay(matched?.display);

  // With no rules nothing matches: SHOW_ON_MATCH shows nowhere, HIDE_ON_MATCH everywhere.
  if (displayRules.mode === 'SHOW_ON_MATCH') {
    return { displayDelay, display: matched ? (onMatch ?? CHAT_BAR_DISPLAY.BAR) : CHAT_BAR_DISPLAY.HIDDEN };
  }
  if (displayRules.mode === 'HIDE_ON_MATCH') {
    return { displayDelay, display: matched ? (onMatch ?? CHAT_BAR_DISPLAY.HIDDEN) : fallback };
  }
  // Compile error when DISPLAY_RULE_MODES gains a member: decide its polarity above.
  displayRules.mode satisfies never;
  return { displayDelay: 0, display: CHAT_BAR_DISPLAY.HIDDEN }; // unrecognized mode escaped validation; no default polarity
}
